import React, { useEffect, useState } from 'react';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { InputText } from 'primereact/inputtext';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgArrow from '@icons/arrow-up.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';

import { Calendar } from 'primereact/calendar';
import dayjs from 'dayjs';
import { shortDateFormat } from '@utils/dateWithTz';
import { useTranslation } from 'react-i18next';
import { InputTextarea } from 'primereact/inputtextarea';
import { TransportationTypeEnum } from '@api/ringil-api.v2.tsx';

interface TransportLegItemProps {
  idx: number;
  leg: any;
  transportationTypes: { code: string; label: string }[];
  navyShippingCompanies: {
    id: string;
    name: string;
    active: boolean;
  }[];
  legsLength: number;
  onChange: (idx: number, updatedLeg: any | null, action?: 'moveUp' | 'moveDown') => void;
}

const TransportLegItem: React.FC<TransportLegItemProps> = ({
                                                             idx,
                                                             leg,
                                                             transportationTypes,
                                                             navyShippingCompanies,
                                                             legsLength,
                                                             onChange,
                                                           }) => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();

  const [routeEnabled, setRouteEnabled] = useState<boolean>(leg.route);
  const [datesEnabled, setDatesEnabled] = useState<boolean>(leg.fromDate || leg.toDate);
  const [noteEnabled, setNoteEnabled] = useState<boolean>(leg.note);
  const [flightNumberEnabled, setFlightNumberEnabled] = useState<boolean>(leg.flightNumber);
  const [flightTypeEnabled, setFlightTypeEnabled] = useState<boolean>(leg.flightType);
  const [trainNumberEnabled, setTrainNumberEnabled] = useState<boolean>(leg.trainNumber);
  const [trainTypeEnabled, setTrainTypeEnabled] = useState<boolean>(leg.trainType);
  const [vehicleTypeEnabled, setVehicleTypeEnabled] = useState<boolean>(leg.vehicleType);
  const [voyageNumberEnabled, setVoyageNumberEnabled] = useState<boolean>(!!leg.voyageNumber);
  const [shipTypeEnabled, setShipTypeEnabled] = useState<boolean>(!!leg.shipType);
  const [shipCompanyIdEnabled, setShipCompanyIdEnabled] = useState<boolean>(!!leg.shipCompanyId);

  const isBoat = leg.transportationType === TransportationTypeEnum.BOAT;
  const isGround = leg.transportationType === TransportationTypeEnum.GROUND;
  const isTrain = leg.transportationType === TransportationTypeEnum.TRAIN;
  const isAir = leg.transportationType === TransportationTypeEnum.AIR;

  const activeCompanies = navyShippingCompanies.filter(c => c.active);

  useEffect(() => {
    setRouteEnabled(!!leg.route);
    setDatesEnabled(!!leg.fromDate || !!leg.toDate);
    setNoteEnabled(!!leg.note);
    setFlightNumberEnabled(!!leg.flightNumber);
    setFlightTypeEnabled(!!leg.flightType);
    setTrainNumberEnabled(!!leg.trainNumber);
    setTrainTypeEnabled(!!leg.trainType);
    setVehicleTypeEnabled(!!leg.vehicleType);
    setVoyageNumberEnabled(!!leg.voyageNumber);
    setShipTypeEnabled(!!leg.shipType);
    setShipCompanyIdEnabled(!!leg.shipCompanyId);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-8 w-full items-end justify-between">
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.transportType.label', 'Transportation type')}
            required={true}
            width={'100%'}
          >
            <Dropdown
              options={transportationTypes}
              value={leg.transportationType}
              optionLabel="label"
              optionValue="code"
              onChange={e => {
                const newType = e.value;

                if (newType !== TransportationTypeEnum.AIR) {
                  setFlightNumberEnabled(false);
                  setFlightTypeEnabled(false);
                }

                if (newType !== TransportationTypeEnum.TRAIN) {
                  setTrainNumberEnabled(false);
                  setTrainTypeEnabled(false);
                }

                if (newType !== TransportationTypeEnum.GROUND) {
                  setVehicleTypeEnabled(false);
                }

                if (newType !== TransportationTypeEnum.BOAT) {
                  setVoyageNumberEnabled(false);
                  setShipTypeEnabled(false);
                  setShipCompanyIdEnabled(false);
                }

                const updatedLeg = {
                  ...leg,
                  transportationType: newType,
                  ...(newType !== TransportationTypeEnum.AIR && {
                    flightNumber: undefined,
                    flightType: undefined,
                  }),
                  ...(newType !== TransportationTypeEnum.TRAIN && {
                    trainNumber: undefined,
                    trainType: undefined,
                  }),
                  ...(newType !== TransportationTypeEnum.GROUND && {
                    vehicleType: undefined,
                  }),
                  ...(newType !== TransportationTypeEnum.BOAT && {
                    voyageNumber: undefined,
                    shipType: undefined,
                    shipCompanyId: undefined,
                  }),
                };
                onChange(idx, updatedLeg);
              }}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => onChange(idx, null)}
              className="p-4"
            />
          </div>
        </div>
        <div className="flex flex-row gap-2 h-9 pb-1 w-[90px] items-end justify-between">
          <RingilButton
            mode={'table'}
            icon={<SvgArrow />}
            disabled={idx === 0}
            onClick={() => onChange(idx, leg, 'moveUp')}
          />
          <RingilButton
            mode={'table'}
            icon={<SvgArrow className="rotate-180" />}
            disabled={idx === legsLength - 1}
            onClick={() => onChange(idx, leg, 'moveDown')}
          />
        </div>
      </div>
      {routeEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.route.label', 'Route')}
            width={'100%'}
          >
            <InputText
              value={leg.route || ''}
              onChange={e => onChange(idx, { ...leg, route: e.target.value })}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, route: undefined });
                setRouteEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {datesEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <div className="flex flex-row gap-2">
            <Field
              label={tr('carrier.demand.quote.transportLegs.fromDate.label' + leg.transportationType, 'Departure date')}
            >
              <Calendar
                locale={i18n.language}
                selectionMode={'single'}
                value={leg.fromDate ? new Date(leg.fromDate) : null}
                onChange={e =>
                  onChange(
                    idx,
                    {
                      ...leg,
                      fromDate: e.value && e.value instanceof Date ? dayjs(e.value).format('YYYY-MM-DD') : null,
                    },
                  )
                }
                showIcon
                dateFormat={shortDateFormat}
                readOnlyInput
                showButtonBar={true}
                showWeek={true}
              />
            </Field>
            <Field
              label={tr('carrier.demand.quote.transportLegs.toDate.label', 'Arrival date')}
            >
              <Calendar
                locale={i18n.language}
                selectionMode={'single'}
                value={leg.toDate ? new Date(leg.toDate) : null}
                onChange={e =>
                  onChange(
                    idx,
                    {
                      ...leg,
                      toDate: e.value && e.value instanceof Date ? dayjs(e.value).format('YYYY-MM-DD') : null,
                    },
                  )
                }
                showIcon
                dateFormat={shortDateFormat}
                readOnlyInput
                showButtonBar={true}
                showWeek={true}
              />
            </Field>
          </div>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, fromDate: undefined, toDate: undefined });
                setDatesEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {isAir && flightNumberEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.flightNumber.label', 'Flight number')}
            width={'100%'}
          >
            <InputText
              value={leg.flightNumber || ''}
              onChange={e => onChange(idx, { ...leg, flightNumber: e.target.value })}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, flightNumber: undefined });
                setFlightNumberEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {isAir && flightTypeEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.flightType.label', 'Flight type')}
            width={'100%'}
          >
            <InputText
              value={leg.flightType || ''}
              onChange={e => onChange(idx, { ...leg, flightType: e.target.value })}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, flightType: undefined });
                setFlightTypeEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {isTrain && trainNumberEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.trainNumber.label', 'Train number')}
            width={'100%'}
          >
            <InputText
              value={leg.trainNumber || ''}
              onChange={e => onChange(idx, { ...leg, trainNumber: e.target.value })}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, trainNumber: undefined });
                setTrainNumberEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {isTrain && trainTypeEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.trainType.label', 'Train type')}
            width={'100%'}
          >
            <InputText
              value={leg.trainType || ''}
              onChange={e => onChange(idx, { ...leg, trainType: e.target.value })}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, trainType: undefined });
                setTrainTypeEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {isGround && vehicleTypeEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.vehicleType.label', 'Vehicle type')}
            width={'100%'}
          >
            <InputText
              value={leg.vehicleType || ''}
              onChange={e => onChange(idx, { ...leg, vehicleType: e.target.value })}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, vehicleType: undefined });
                setVehicleTypeEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {isBoat && voyageNumberEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.voyageNumber.label', 'Voyage number')}
            width={'100%'}
          >
            <InputText
              value={leg.voyageNumber || ''}
              onChange={e => onChange(idx, { ...leg, voyageNumber: e.target.value })}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, voyageNumber: undefined });
                setVoyageNumberEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {isBoat && shipTypeEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.shipType.label', 'Ship type')}
            width={'100%'}
          >
            <InputText
              value={leg.shipType || ''}
              onChange={e => onChange(idx, { ...leg, shipType: e.target.value })}
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, shipType: undefined });
                setShipTypeEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {isBoat && shipCompanyIdEnabled && (
        <div className="flex flex-row gap-2 w-full items-end justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.shipCompanyId.label', 'Ship company')}
            width={'100%'}
          >
            <Dropdown
              options={activeCompanies}
              value={leg.shipCompanyId || null}
              optionLabel="name"
              optionValue="id"
              placeholder={tr('carrier.demand.quote.transportLegs.shipCompanyId.placeholder', 'Select company')}
              onChange={e => onChange(idx, { ...leg, shipCompanyId: e.value })}
              showClear
              filter
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, shipCompanyId: undefined });
                setShipCompanyIdEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      {noteEnabled && (
        <div className="flex flex-row gap-2 w-full items-center justify-between">
          <Field
            label={tr('carrier.demand.quote.transportLegs.note.label', 'Note')}
            width={'100%'}
          >
            <InputTextarea
              value={leg.note || ''}
              onChange={e => onChange(idx, { ...leg, note: e.target.value })}
              rows={3}
              autoResize
            />
          </Field>
          <div className="h-9 pb-1">
            <RingilButton
              mode={'table'}
              icon={<SvgDelete />}
              onClick={() => {
                onChange(idx, { ...leg, note: undefined });
                setNoteEnabled(false);
              }}
              className="p-4"
            />
          </div>
        </div>
      )}

      <div className="flex flex-row mt-2 gap-2 justify-start flex-wrap">
        {!routeEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.routeEnabled.label', 'Add route')}
            onClick={() => setRouteEnabled(true)}
          />
        )}
        {!datesEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.datesEnabled.label', 'Add dates')}
            onClick={() => setDatesEnabled(true)}
          />
        )}
        {isAir && !flightNumberEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.flightNumberEnabled.label', 'Add flight number')}
            onClick={() => setFlightNumberEnabled(true)}
          />
        )}
        {isAir && !flightTypeEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.flightTypeEnabled.label', 'Add flight type')}
            onClick={() => setFlightTypeEnabled(true)}
          />
        )}
        {isTrain && !trainNumberEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.trainNumberEnabled.label', 'Add train number')}
            onClick={() => setTrainNumberEnabled(true)}
          />
        )}
        {isTrain && !trainTypeEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.trainTypeEnabled.label', 'Add train type')}
            onClick={() => setTrainTypeEnabled(true)}
          />
        )}
        {isGround && !vehicleTypeEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.vehicleTypeEnabled.label', 'Add vehicle type')}
            onClick={() => setVehicleTypeEnabled(true)}
          />
        )}
        {isBoat && !voyageNumberEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.voyageNumberEnabled.label', 'Add voyage number')}
            onClick={() => setVoyageNumberEnabled(true)}
          />
        )}
        {isBoat && !shipTypeEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.shipTypeEnabled.label', 'Add ship type')}
            onClick={() => setShipTypeEnabled(true)}
          />
        )}
        {isBoat && !shipCompanyIdEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.shipCompanyIdEnabled.label', 'Add ship company')}
            onClick={() => setShipCompanyIdEnabled(true)}
          />
        )}
        {!noteEnabled && (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('carrier.demand.quote.transportLegs.button.noteEnabled.label', 'Add note')}
            onClick={() => setNoteEnabled(true)}
          />
        )}
      </div>
    </div>
  );
};

export default TransportLegItem;